import {
  ConfirmationModal,
  ErrorMessage,
  TextInput,
  Tooltip,
  UPDATE_CURRENT_COMPANY,
} from '@employ-engage/common-components';
import { ManagementRole } from '@employ-engage/data';
import { useFormik } from 'formik';
import { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import kebabCase from 'lodash.kebabcase';

export interface ManagementRolesFormProps {
  managementRoles?: ManagementRole[];
  selectNewRole?: (role: ManagementRole) => void;
}

export function ManagementRolesForm({
  managementRoles,
  selectNewRole,
}: ManagementRolesFormProps) {
  const { t } = useTranslation(['common']);
  const [confirmDeleteRoleIndex, setConfirmDeleteRoleIndex] = useState<
    number | null
  >(null);
  const [updateCompany] = useMutation(UPDATE_CURRENT_COMPANY);
  const { values, setFieldValue, handleSubmit, handleChange, errors, touched } =
    useFormik({
      initialValues: {
        managementRoles: managementRoles ? managementRoles : [],
        newManagementRole: {
          id: '',
          name: '',
        },
      },
      onSubmit: (values) => {
        const newManagementRoles = [
          ...values.managementRoles.map(
            (role) => ({ id: role.id, name: role.name } as ManagementRole)
          ),
          values.newManagementRole,
        ];

        updateCompany({
          variables: {
            input: {
              managementRoles: newManagementRoles,
            },
          },
        })
          .then(() => {
            setFieldValue('managementRoles', newManagementRoles);
            setFieldValue('newManagementRole', {
              id: '',
              name: '',
            });
            selectNewRole?.(values.newManagementRole);
          })
          .catch((error) => {
            console.error('Error updating management roles:', error);
          });
      },
    });

  const onRemoveRole = (index: number) => {
    const newManagementRoles = [...values.managementRoles];
    newManagementRoles.splice(index, 1);

    updateCompany({
      variables: {
        input: {
          managementRoles: newManagementRoles,
        },
      },
    }).then(() => {
      setFieldValue('managementRoles', newManagementRoles);
      setFieldValue('newManagementRole', {
        id: '',
        name: '',
      });
    });
  };

  return (
    <form className="mt-4 space-y-2.5" onSubmit={handleSubmit}>
      <ul>
        {values.managementRoles.map((item, index) => {
          return (
            <li key={item.id} className="flex items-center gap-2">
              <span>{t(item.name)}</span>
              <button
                type="button"
                onClick={() => setConfirmDeleteRoleIndex(index)}
              >
                <i className="fa-solid fa-trash cursor-pointer text-semantic-error" />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="col-span-2">
        <label
          htmlFor="newManagementRole"
          className="block text-sm font-medium text-gray-700"
        >
          {t('New Role')}
        </label>
        <div className="flex items-center gap-x-2">
          <TextInput
            id="newManagementRole.name"
            type="text"
            className="add-role-input"
            value={values.newManagementRole.name}
            placeholder={t('e.g. "Manager"')}
            handleChange={(event) => {
              setFieldValue(
                'newManagementRole.id',
                kebabCase(event.target.value)
              );
              handleChange(event);
            }}
          />
          <Tooltip label={t('Add new role')}>
            <button
              className="add-role-icon-button"
              type="submit"
              disabled={!values.newManagementRole}
            >
              <i
                className={classNames('fa-solid fa-circle-plus cursor-pointer')}
              />
            </button>
          </Tooltip>
        </div>
        <ErrorMessage
          errors={errors.newManagementRole}
          touched={touched.newManagementRole}
        />
      </div>
      <ConfirmationModal
        text={t('confirm-role-deletion', {
          role: values.managementRoles[confirmDeleteRoleIndex || 0]?.name,
        })}
        open={confirmDeleteRoleIndex !== null}
        setOpen={() => setConfirmDeleteRoleIndex(null)}
        onConfirm={() => {
          onRemoveRole(confirmDeleteRoleIndex as number);
          setConfirmDeleteRoleIndex(null);
        }}
        onCancel={() => setConfirmDeleteRoleIndex(null)}
      />
    </form>
  );
}

export default ManagementRolesForm;
